import { IdeaListItem } from '@/apis/idea/idea-api.def.ts'
import Card from 'antd/es/card'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CaretDownFilled,
  CloseCircleFilled,
  DownOutlined,
  EditTwoTone,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons'
import { FillingProgress } from '@/pages/idea-list/components/filling-progress.tsx'
import Tooltip from 'antd/es/tooltip'
import { useAppSelector } from '@/redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '@/features/setting/setting.slice.ts'
import { memo, useMemo, useState } from 'react'
import { isHistoricalEndDate } from '@/utils/time.ts'
import './idea-card.scss'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { IdeaDirectionTag } from '@/pages/idea-list/components/idea-list-table/idea-direction-tag.tsx'
import { useTranslation } from 'react-i18next'
import {
  currencyValueFormatter,
  getCheckStateNumberColor,
  marketDateTimezoneFormatter,
  percentageValueFormatter,
} from '@/utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { selectUser } from '@/features/user/user.slice.ts'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '@/utils/permissions.ts'
import { isDisabledIdeaState } from '@/enums/management-input.enum.ts'
import { useLazyGetIdeaOrdersQuery } from '@/apis/idea/idea-api.api.ts'
import type { UnifiedIdeaRow } from '@/pages/idea-list/components/idea-list-table/idea-list-ssrm-adapter.ts'
import { IdeyaSpinner } from '@/common/components/ideya-spinner.tsx'
import { IdeaAlertType } from '@/enums/idea-list.enum.ts'
import { Theme } from '@/features/setting/setting.def.ts'
import { MOBILE_VIEW_CARD_FIELD_LABEL_KEYS } from '@/common/constants/idea-list.constants.ts'

type OrderSortColumn = 'time' | 'fillProgress' | 'currentMV'
type OrderSortDirection = 'asc' | 'desc'

// Sorting is temporarily disabled; keep the logic behind this flag so it can
// be re-enabled without reworking the header.
const ORDER_SORTING_ENABLED = false

// Number of order rows shown before "Show more" is required.
const DEFAULT_VISIBLE_ORDERS = 3

interface IdeaCardProps {
  idea: IdeaListItem
  onEditIdea: (id: string) => void
}

export const IdeaCard = memo(({ idea, onEditIdea }: IdeaCardProps) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const colorTheme = useAppSelector(selectTheme)
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)
  const [showAllOrders, setShowAllOrders] = useState<boolean>(false)
  const userInfo = useAppSelector(selectUser)
  // Select single fields so unrelated filter changes don't re-render every card.
  const endDate = useAppSelector((state) => state.ideaListInput.dateRange[1])
  const cardFields = useAppSelector(
    (state) => state.ideaListInput.mobileViewCardFields
  )

  // When viewing a historical end date, point-in-time metrics are shown as '-'
  const isHistorical = isHistoricalEndDate(endDate)

  const [sortColumn, setSortColumn] = useState<OrderSortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<OrderSortDirection | null>(
    null
  )

  const [triggerGetIdeaOrders, { data: orders, isFetching: ordersFetching }] =
    useLazyGetIdeaOrdersQuery()

  const isDisabled = isDisabledIdeaState(idea.state)
  const disabledTextColor = isDisabled
    ? themeStyle.colorTextDisabled
    : undefined

  const canEditIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )
  const canEdit = idea.state !== 'Liquidate' && idea.canEdit && canEditIdea

  const toggleExpand = () => {
    const next = !expanded
    setExpanded(next)
    if (next) {
      if (!orders && !ordersFetching) {
        triggerGetIdeaOrders(idea.id)
      }
    } else {
      // Reset to the default 3-row view so re-expanding always starts collapsed.
      setShowAllOrders(false)
    }
  }

  const cycleSort = (column: OrderSortColumn) => {
    if (sortColumn !== column) {
      setSortColumn(column)
      setSortDirection('asc')
      return
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc')
      return
    }
    // Third tap clears the sort back to the API's default order.
    setSortColumn(null)
    setSortDirection(null)
  }

  const sortedOrderRows = useMemo<UnifiedIdeaRow[]>(() => {
    const rows = orders ?? []
    if (!ORDER_SORTING_ENABLED || !sortColumn || !sortDirection) return rows

    const getValue = (row: UnifiedIdeaRow): number => {
      switch (sortColumn) {
        case 'time':
          return row.createdAt ? new Date(row.createdAt).getTime() : 0
        case 'fillProgress':
          return row.fillingProgress ?? 0
        case 'currentMV':
          return row.currentMarketValue ?? 0
      }
    }

    return [...rows].sort((a, b) => {
      const diff = getValue(a) - getValue(b)
      return sortDirection === 'asc' ? diff : -diff
    })
  }, [orders, sortColumn, sortDirection])

  const hasMoreOrders = sortedOrderRows.length > DEFAULT_VISIBLE_ORDERS
  const initialOrders = sortedOrderRows.slice(0, DEFAULT_VISIBLE_ORDERS)
  const extraOrders = sortedOrderRows.slice(DEFAULT_VISIBLE_ORDERS)

  const alertType = idea.showAlert ? idea.alert?.type : undefined
  const alertIcon = (() => {
    switch (alertType) {
      case IdeaAlertType.Error:
        return (
          <CloseCircleFilled
            className={'idea-card__alert-icon idea-card__alert-icon--error'}
          />
        )
      case IdeaAlertType.Warning:
        return (
          <ExclamationCircleFilled
            className={'idea-card__alert-icon idea-card__alert-icon--warning'}
          />
        )
      case IdeaAlertType.Info:
        return (
          <InfoCircleFilled
            className={'idea-card__alert-icon idea-card__alert-icon--info'}
          />
        )
      default:
        return null
    }
  })()

  const orderValueColor = isDisabled ? 'var(--color-text-disabled)' : undefined

  return (
    <Card
      className={'idea-card'}
      styles={{
        body: {
          padding: 0,
          border: `1px solid ${themeStyle.colorBorder}`,
          borderRadius: themeStyle.borderRadiusLG,
          overflow: 'hidden',
        },
      }}
    >
      <div
        className={'idea-card__clickable'}
        role={'button'}
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleExpand()
          }
        }}
      >
        <Row
          className={'idea-card__header'}
          align={'middle'}
          justify={'space-between'}
          wrap={false}
        >
          <Col>
            <Row align={'middle'} gutter={8} wrap={false}>
              <Col>
                <IdeyaTypography.Text
                  className={'idea-card__ticker-name'}
                  style={{ color: disabledTextColor }}
                >
                  {idea.bbgCompositeKey}
                </IdeyaTypography.Text>
              </Col>
              <Col>
                <IdeaDirectionTag direction={idea.direction} />
              </Col>
              {alertIcon && (
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip
                    title={idea.alert?.message}
                    color={colorTheme === Theme.Light ? 'white' : undefined}
                    styles={{
                      body: {
                        color:
                          colorTheme === Theme.Light
                            ? themeStyle.colorText
                            : undefined,
                      },
                    }}
                  >
                    <span
                      className={'idea-card__alert'}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {alertIcon}
                    </span>
                  </Tooltip>
                </Col>
              )}
            </Row>
          </Col>
          <Col>
            <Row align={'middle'} gutter={10} wrap={false}>
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <DownOutlined
                  className={'idea-card__chevron'}
                  style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: disabledTextColor,
                  }}
                />
              </Col>
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  type={'button'}
                  aria-label={t('idea_list.idea_table.edit')}
                  className={'idea-card__edit-btn'}
                  disabled={!canEdit}
                  style={{
                    backgroundColor: themeStyle.blue6,
                    border: `1px solid ${themeStyle.colorPrimaryBorder}`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (canEdit) {
                      onEditIdea(idea.id)
                    }
                  }}
                >
                  <EditTwoTone twoToneColor={themeStyle.blue10} />
                </button>
              </Col>
            </Row>
          </Col>
        </Row>

        <div
          className={'idea-card__details'}
          style={{ borderBottom: `1px solid ${themeStyle.colorBorder}` }}
        >
          <div className={'idea-card__legal-name'}>
            <IdeyaTypography.Text
              type={'secondary'}
              style={{
                fontSize: themeStyle.fontSizeSM,
                lineHeight: '18px',
                color: disabledTextColor,
              }}
            >
              {idea.tickerLegalName}
            </IdeyaTypography.Text>
          </div>
          <Row justify={'space-between'} wrap={false}>
            {cardFields.map((field) => {
              const value = idea[field]
              // Mirrors the table columns: daily PnL stays live on historical
              // end dates, the other point-in-time metrics show '-'.
              const hidden = isHistorical && field !== 'dailyPnl'
              const isPct =
                field === 'dailyReturnPct' || field === 'quarterlyReturnPct'
              return (
                <Col key={field}>
                  <Row>
                    <IdeyaTypography.Text
                      type={'secondary'}
                      className={'idea-card__metric-label'}
                    >
                      {t(MOBILE_VIEW_CARD_FIELD_LABEL_KEYS[field])}
                    </IdeyaTypography.Text>
                  </Row>
                  <Row>
                    <IdeyaTypography.Text
                      className={'idea-card__metric-value'}
                      style={{
                        color: hidden
                          ? disabledTextColor
                          : getCheckStateNumberColor(value, idea.state),
                      }}
                    >
                      {hidden
                        ? '-'
                        : isPct
                          ? percentageValueFormatter({
                              value,
                            } as ValueFormatterParams)
                          : currencyValueFormatter(
                              { value } as ValueFormatterParams,
                              { decimal: 0, currency: 'USD' }
                            )}
                    </IdeyaTypography.Text>
                  </Row>
                </Col>
              )
            })}
            <Col className={'idea-card__col-end'}>
              <Row justify={'end'}>
                <IdeyaTypography.Text
                  type={'secondary'}
                  className={'idea-card__metric-label'}
                >
                  {t('idea_list.idea_table.columns.current_mv')}
                </IdeyaTypography.Text>
              </Row>
              <Row justify={'end'}>
                <IdeyaTypography.Text
                  className={'idea-card__metric-value'}
                  style={{ color: disabledTextColor }}
                >
                  {currencyValueFormatter(
                    {
                      value: idea.currentMarketValue,
                    } as ValueFormatterParams,
                    { decimal: 0, currency: 'USD' }
                  )}
                </IdeyaTypography.Text>
              </Row>
            </Col>
          </Row>
        </div>
      </div>

      <div
        className={`idea-card__expand-wrapper${expanded ? ' idea-card__expand-wrapper--open' : ''}`}
      >
        <div className={'idea-card__expand-inner'}>
          <div
            className={'idea-card__more'}
            style={{ backgroundColor: themeStyle.cardMoreBg }}
          >
            <Row
              className={'idea-card__more-header'}
              justify={'space-between'}
              wrap={false}
              style={{ borderBottom: `1px solid ${themeStyle.colorBorder}` }}
            >
              <Col className={'idea-card__more-col'}>
                <SortableHeader
                  label={t('idea_list.idea_table.columns.time')}
                  column={'time'}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={cycleSort}
                  activeColor={themeStyle.colorPrimaryText}
                  sortingEnabled={ORDER_SORTING_ENABLED}
                />
              </Col>
              <Col className={'idea-card__more-col'}>
                <SortableHeader
                  label={t('idea_list.idea_table.columns.filling_progress')}
                  column={'fillProgress'}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={cycleSort}
                  activeColor={themeStyle.colorPrimaryText}
                  sortingEnabled={ORDER_SORTING_ENABLED}
                />
              </Col>
              <Col className={'idea-card__more-col idea-card__more-col--end'}>
                <SortableHeader
                  label={t('idea_list.idea_table.columns.current_mv')}
                  column={'currentMV'}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={cycleSort}
                  activeColor={themeStyle.colorPrimaryText}
                  align={'end'}
                  sortingEnabled={ORDER_SORTING_ENABLED}
                />
              </Col>
            </Row>

            {ordersFetching ? (
              <div className={'idea-card__more-loading'}>
                <IdeyaSpinner />
              </div>
            ) : sortedOrderRows.length === 0 ? (
              <Row className={'idea-card__more-row'} justify={'center'}>
                <IdeyaTypography.Text
                  type={'secondary'}
                  className={'idea-card__order-text'}
                >
                  -
                </IdeyaTypography.Text>
              </Row>
            ) : (
              <>
                {initialOrders.map((order) => {
                  return (
                    <Row
                      key={order.orderId}
                      className={'idea-card__more-row'}
                      align={'middle'}
                      justify={'space-between'}
                      wrap={false}
                      style={{ backgroundColor: themeStyle.cardOrderRowBg }}
                    >
                      <Col className={'idea-card__more-col'}>
                        <IdeyaTypography.Text
                          className={'idea-card__order-text'}
                          style={{ color: orderValueColor }}
                        >
                          {order.createdAt
                            ? marketDateTimezoneFormatter(
                                {
                                  value: String(order.createdAt),
                                  data: { marketTimeZone: idea.marketTimeZone },
                                },
                                { timeOnly: true }
                              )
                            : '-'}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        className={
                          'idea-card__more-col idea-card__more-col--progress'
                        }
                      >
                        <FillingProgress
                          value={order.fillingProgress}
                          ideaState={idea.state}
                          orderState={order.state}
                          isOrderRow={true}
                        />
                      </Col>
                      <Col
                        className={
                          'idea-card__more-col idea-card__more-col--end'
                        }
                      >
                        <IdeyaTypography.Text
                          className={'idea-card__order-text'}
                          style={{ color: orderValueColor }}
                        >
                          {currencyValueFormatter(
                            {
                              value: order.currentMarketValue,
                            } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                  )
                })}

                {hasMoreOrders && (
                  <div
                    className={`idea-card__expand-wrapper${showAllOrders ? ' idea-card__expand-wrapper--open' : ''}`}
                  >
                    <div className={'idea-card__expand-inner'}>
                      {extraOrders.map((order) => (
                        <Row
                          key={order.orderId}
                          className={'idea-card__more-row'}
                          align={'middle'}
                          justify={'space-between'}
                          wrap={false}
                          style={{ backgroundColor: themeStyle.cardOrderRowBg }}
                        >
                          <Col className={'idea-card__more-col'}>
                            <IdeyaTypography.Text
                              className={'idea-card__order-text'}
                              style={{ color: orderValueColor }}
                            >
                              {order.createdAt
                                ? marketDateTimezoneFormatter(
                                    {
                                      value: String(order.createdAt),
                                      data: {
                                        marketTimeZone: idea.marketTimeZone,
                                      },
                                    },
                                    { timeOnly: true }
                                  )
                                : '-'}
                            </IdeyaTypography.Text>
                          </Col>
                          <Col
                            className={
                              'idea-card__more-col idea-card__more-col--progress'
                            }
                          >
                            <FillingProgress
                              value={order.fillingProgress}
                              ideaState={idea.state}
                              orderState={order.state}
                              isOrderRow={true}
                            />
                          </Col>
                          <Col
                            className={
                              'idea-card__more-col idea-card__more-col--end'
                            }
                          >
                            <IdeyaTypography.Text
                              className={'idea-card__order-text'}
                              style={{ color: orderValueColor }}
                            >
                              {currencyValueFormatter(
                                {
                                  value: order.currentMarketValue,
                                } as ValueFormatterParams,
                                { decimal: 0, currency: 'USD' }
                              )}
                            </IdeyaTypography.Text>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  </div>
                )}

                {hasMoreOrders && (
                  <button
                    type={'button'}
                    className={'idea-card__show-more'}
                    aria-expanded={showAllOrders}
                    style={{ color: themeStyle.colorPrimaryText }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowAllOrders((prev) => !prev)
                    }}
                  >
                    <CaretDownFilled
                      className={'idea-card__chevron'}
                      style={{
                        transform: showAllOrders
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                      }}
                    />
                    <IdeyaTypography.Text
                      className={'idea-card__show-more-text'}
                      style={{ color: themeStyle.colorPrimaryText }}
                    >
                      {showAllOrders
                        ? t('idea_list.idea_table.show_less')
                        : t('idea_list.idea_table.show_more')}
                    </IdeyaTypography.Text>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
})

IdeaCard.displayName = 'IdeaCard'

interface SortableHeaderProps {
  label: string
  column: OrderSortColumn
  sortColumn: OrderSortColumn | null
  sortDirection: OrderSortDirection | null
  onSort: (column: OrderSortColumn) => void
  activeColor?: string
  align?: 'start' | 'end'
  sortingEnabled?: boolean
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  column,
  sortColumn,
  sortDirection,
  onSort,
  activeColor,
  align = 'start',
  sortingEnabled = true,
}) => {
  if (!sortingEnabled) {
    return (
      <div
        className={'idea-card__sort-header idea-card__sort-header--static'}
        style={{
          justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
        }}
      >
        <IdeyaTypography.Text
          type={'secondary'}
          className={'idea-card__metric-label'}
        >
          {label}
        </IdeyaTypography.Text>
      </div>
    )
  }

  const isActive = sortColumn === column
  return (
    <button
      type={'button'}
      className={'idea-card__sort-header'}
      style={{
        justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
      }}
      onClick={(e) => {
        e.stopPropagation()
        onSort(column)
      }}
    >
      <IdeyaTypography.Text
        type={isActive ? undefined : 'secondary'}
        className={'idea-card__metric-label'}
        style={{
          color: isActive ? activeColor : undefined,
        }}
      >
        {label}
      </IdeyaTypography.Text>
      {isActive && sortDirection === 'asc' && (
        <ArrowUpOutlined
          className={'idea-card__sort-icon'}
          style={{ color: activeColor }}
        />
      )}
      {isActive && sortDirection === 'desc' && (
        <ArrowDownOutlined
          className={'idea-card__sort-icon'}
          style={{ color: activeColor }}
        />
      )}
    </button>
  )
}
